/**
 * The live half of a support conversation.
 *
 * Messages arrive on a held SSE stream; everything the reader sends still
 * goes through the ordinary POST endpoints in `support-tickets.ts`. A chat is
 * asymmetric — receiving is frequent and one-way, sending is rare and wants
 * validation, an acknowledgement and, for a screenshot, a multipart body — so
 * the two halves use the transport each is good at.
 *
 * A stream is opened when a conversation is actually on screen, never on page
 * mount. The support bubble sits on every page in ODI; the conversation does
 * not, and one held connection per tab would cost far more than it buys.
 *
 * Nothing here trusts the stream to be complete. `afterMessageId` is what the
 * caller already has, and reconnecting with it is what makes a dropped frame
 * or a dropped connection self-healing.
 */

import { consumeSseStream } from "./sse-frames";
import { SupportAiSuggestion, SupportMessage, SupportTicket } from "./support-tickets";

/** Frames the server sends. `ready` marks the end of the catch-up burst, so a
 *  caller can tell "here is the backlog" from "this just happened". */
export type SupportStreamEvent =
	| { type: "message"; message: SupportMessage }
	| { type: "suggestion"; suggestion: SupportAiSuggestion }
	| { type: "label"; label: string }
	| { type: "ready"; last_id: number };

export interface OpenSupportStreamOptions {
	ticketId: number;
	/** Highest message id already held; the server replays everything after it. */
	afterMessageId: number;
	onMessage: (message: SupportMessage) => void;
	/** Staff only — a finished AI draft. Reporters are never sent these. */
	onSuggestion?: (suggestion: SupportAiSuggestion) => void;
	/** The conversation was named: the AI label landing a moment after the
	 *  ticket was created, while its first message is already on screen. */
	onLabel?: (label: string) => void;
	/** Fired once the backlog has been delivered. */
	onReady?: (lastId: number) => void;
	signal: AbortSignal;
}

/** How long to wait before reconnecting a stream that dropped, and the ceiling
 *  it backs off to. A conversation is worth reconnecting promptly; a server
 *  that is down is not worth hammering. */
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15000;

function streamUrl(ticketId: number, afterMessageId: number): string {
	const params = new URLSearchParams({ after: String(afterMessageId) });
	return `/api/stream/support-ticket/${ticketId}/?${params.toString()}`;
}

/**
 * Follow one conversation until the caller aborts.
 *
 * Resolves only when aborted. Each reconnect resumes from the highest id
 * actually delivered, so nothing is missed across a dropped connection and
 * nothing is delivered twice.
 */
export async function followSupportTicket(
	opts: OpenSupportStreamOptions
): Promise<void> {
	let after = opts.afterMessageId;
	let attempt = 0;

	while (!opts.signal.aborted) {
		try {
			await consumeSseStream<SupportStreamEvent>(
				streamUrl(opts.ticketId, after),
				opts.signal,
				(event) => {
					if (event.type === "message") {
						after = Math.max(after, event.message.id);
						opts.onMessage(event.message);
						return;
					}
					if (event.type === "suggestion") {
						opts.onSuggestion?.(event.suggestion);
						return;
					}
					if (event.type === "label") {
						opts.onLabel?.(event.label);
						return;
					}
					after = Math.max(after, event.last_id);
					opts.onReady?.(event.last_id);
				}
			);
			// A clean close is still a close: reconnect, from where we got to.
			attempt = 0;
		} catch (error) {
			if ((error as Error).name === "AbortError") return;
			attempt += 1;
		}
		if (opts.signal.aborted) return;
		const wait = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
		await new Promise((resolve) => setTimeout(resolve, wait));
	}
}

/** Frames the queue stream sends. A `ticket` is the whole row, re-read after
 *  something moved it, in the same shape `fetchSupportTickets` returns — so a
 *  caller merges it by id and every filter and count stays right. */
export type SupportQueueEvent =
	| { type: "ticket"; ticket: SupportTicket }
	| { type: "ready" };

export interface OpenSupportQueueOptions {
	onTicket: (ticket: SupportTicket) => void;
	/**
	 * Fired on connect AND on every reconnect.
	 *
	 * The queue stream sends no backlog, so this is where a caller re-fetches:
	 * on the first connect the list was just loaded and the re-fetch is
	 * harmless, and on a reconnect it is the only thing that repairs whatever
	 * moved while the connection was down.
	 */
	onReady?: () => void;
	signal: AbortSignal;
}

/**
 * Follow the whole support queue until the caller aborts.
 *
 * One connection for the operator's /support page, not one per row: the queue
 * lists conversations nobody has necessarily opened, so a stream per ticket
 * would mean dozens of held connections for a single page.
 */
export async function followSupportQueue(opts: OpenSupportQueueOptions): Promise<void> {
	let attempt = 0;

	while (!opts.signal.aborted) {
		try {
			await consumeSseStream<SupportQueueEvent>(
				"/api/stream/support-tickets/",
				opts.signal,
				(event) => {
					if (event.type === "ticket") {
						opts.onTicket(event.ticket);
						return;
					}
					opts.onReady?.();
				}
			);
			attempt = 0;
		} catch (error) {
			if ((error as Error).name === "AbortError") return;
			attempt += 1;
		}
		if (opts.signal.aborted) return;
		const wait = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
		await new Promise((resolve) => setTimeout(resolve, wait));
	}
}

/** The badge stream sends one number, and only when it has changed. */
export type SupportUnreadEvent = { type: "unread"; count: number };

export interface OpenSupportUnreadOptions {
	onCount: (count: number) => void;
	signal: AbortSignal;
}

/**
 * Follow the bubble's unread badge until the caller aborts.
 *
 * This replaces a 60-second poll. Every signed-in tab in ODI holds one of
 * these, which is affordable only because the stream service is an event loop
 * — an idle connection there is a few KB, while a poll was a request per tab
 * per minute from every person using the application.
 */
export async function followSupportUnread(
	opts: OpenSupportUnreadOptions
): Promise<void> {
	let attempt = 0;

	while (!opts.signal.aborted) {
		try {
			await consumeSseStream<SupportUnreadEvent>(
				"/api/stream/support-unread/",
				opts.signal,
				(event) => opts.onCount(event.count)
			);
			attempt = 0;
		} catch (error) {
			if ((error as Error).name === "AbortError") return;
			attempt += 1;
		}
		if (opts.signal.aborted) return;
		const wait = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
		await new Promise((resolve) => setTimeout(resolve, wait));
	}
}
